import { useEffect, useRef, type CSSProperties } from 'react';
import './scroll.css';

/**
 * The band under the hero: one claim, one big number, and the bar it
 * counts along.
 *
 * The number is not a score. Krevya has no measured win rate to show and this
 * page does not invent one; what it counts is true of the product itself: the
 * four steps from a product link to a store-page draft, all of which Krevya
 * runs. The bar fills with the reader's scroll through the band and the stages
 * under it follow the fill; scrolling back runs it backwards. The number reads
 * 4/4 throughout: it is the claim, all four steps are Krevya's, and counting it
 * up from 0/4 read as "not done yet" to anyone who stopped half way.
 * Nothing about the scroll itself is changed.
 *
 * On a phone the bar across would be too narrow to read, so the stages stand
 * on a rail down the left instead, and the same scroll drives it: a line runs
 * down the rail as the reader scrolls, and each stage it reaches lights, its
 * dot filling with a single pulse and the row settling into place from a
 * dimmed, slightly offset start. The stage the line has most recently reached
 * is drawn as a card, so there is always one thing in focus. Scrolling back
 * runs it backwards, like the desktop's bar. A slider that turned by itself
 * was tried first; it asked the reader to wait for it, where this moves
 * exactly as fast as they do.
 */

type Tx = (en: string, tr: string) => string;

const STEPS = 4;
const PHONE = '(max-width: 760px)';
/** Where on the screen the phone's line is drawn to: 70% of the way down. */
const READ_LINE = 0.7;
/** From the top of a stage to the middle of its dot. */
const DOT = 19;

export function PipelineBand({ tx }: { tx: Tx }) {
  const band = useRef<HTMLElement>(null);
  const list = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const el = band.current;
    if (!el) return;
    const ol = list.current;
    const stages = Array.from(el.querySelectorAll<HTMLElement>('[data-stage-at]'));
    const phone = window.matchMedia(PHONE);
    const mark = (reached: boolean[]) => {
      const current = reached.lastIndexOf(true);
      stages.forEach((stage, i) => {
        const value = reached[i] ? 'true' : 'false';
        if (stage.dataset.reached !== value) stage.dataset.reached = value;
        const now = i === current ? 'true' : 'false';
        if (stage.dataset.current !== now) stage.dataset.current = now;
      });
    };
    const paint = (progress: number) => {
      el.style.setProperty('--p', progress.toFixed(3));
      mark(stages.map((stage) => progress >= Number(stage.dataset.stageAt) - 0.001));
    };
    /*
     * The phone's rail. It runs from the first stage's dot to the last one's,
     * and the line on it is drawn down to where the reading line crosses the
     * list; a stage is reached when the line gets to its dot.
     */
    const paintRail = () => {
      const first = stages[0];
      const last = stages[stages.length - 1];
      if (!ol || !first || !last) return;
      const top = first.offsetTop + DOT;
      const bottom = last.offsetTop + DOT;
      const reach = window.innerHeight * READ_LINE - ol.getBoundingClientRect().top;
      const fill = Math.min(1, Math.max(0, (reach - top) / Math.max(1, bottom - top)));
      ol.style.setProperty('--rail-top', `${top}px`);
      ol.style.setProperty('--rail-h', `${bottom - top}px`);
      ol.style.setProperty('--rail', fill.toFixed(3));
      mark(stages.map((stage) => reach >= stage.offsetTop + DOT - 1));
    };

    /*
     * The fill runs the same with reduced motion: a bar filling in place is
     * not movement across the screen, and switching it off is what made the
     * page look broken on a PC with animation effects turned off.
     */
    let frame = 0;
    const measure = () => {
      frame = 0;
      if (phone.matches) {
        paintRail();
        return;
      }
      const box = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Starts as the band's top passes 95% of the screen and is full when
      // it reaches 45%: half a screen of scrolling for the whole run, so the
      // reader sees it count without having to stop and wait for it.
      const raw = (vh * 0.95 - box.top) / (vh * 0.5);
      paint(Math.min(1, Math.max(0, raw)));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    // Only listen while the band is anywhere near the screen.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          window.addEventListener('scroll', onScroll, { passive: true });
          window.addEventListener('resize', onScroll, { passive: true });
          onScroll();
        } else {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        }
      },
      { rootMargin: '20% 0px 20% 0px' },
    );
    observer.observe(el);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const stages: [string, string][] = [
    [tx('Research', 'Araştırma'), tx('Reads your product and competitor pages', 'Ürününü ve rakip sayfaları okur')],
    [tx('Angles', 'Satış açıları'), tx('Benefits, objections and gaps to test', 'Test edilecek fayda, itiraz ve boşluklar')],
    [tx('Creatives', 'Kreatifler'), tx('Ad images in 1:1, 4:5 and 9:16', '1:1, 4:5 ve 9:16 reklam görselleri')],
    [tx('Store page', 'Mağaza sayfası'), tx('A draft on the Krevya Theme', 'Krevya Theme’de bir taslak')],
  ];

  return (
    <section ref={band} className="lp-band lp-staged" data-reveal>
      <h2 data-stage style={{ '--i': 0 } as CSSProperties}>
        {tx(
          'From one product link to a researched brief, ad angles, creatives and a store page.',
          'Tek bir ürün linkinden araştırılmış brife, satış açılarına, kreatiflere ve mağaza sayfasına.',
        )}
      </h2>
      <div className="lp-band-score" data-stage style={{ '--i': 1 } as CSSProperties}>
        <p className="lp-band-number" aria-hidden="true">
          <strong>{STEPS}</strong>
          <span>/{STEPS}</span>
        </p>
        <p className="lp-band-label">
          <span className="sr-only">{tx(`${STEPS} of ${STEPS}`, `${STEPS} adımın ${STEPS}'ü`)}: </span>
          {tx(
            'steps Krevya runs for you, from the product link to a store-page draft.',
            'adımı Krevya senin yerine yürütür: ürün linkinden mağaza sayfası taslağına kadar.',
          )}
        </p>
      </div>
      <div className="lp-meter" data-stage style={{ '--i': 2 } as CSSProperties}>
        <div className="lp-meter-track" aria-hidden="true">
          <i />
          {[1, 2, 3].map((n) => (
            <b key={n} style={{ left: `${(n / STEPS) * 100}%` }} />
          ))}
        </div>
        <ol ref={list}>
          {stages.map(([name, fact], i) => (
            <li key={name} data-stage-at={((i + 1) / STEPS - 0.02).toFixed(3)} data-reached="true">
              <strong>
                <b aria-hidden="true" />
                {name}
              </strong>
              <span>{fact}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
